// src/components/Treatment.js
import React, { useContext, useEffect, useRef, useState } from "react";
import { TextField, MenuItem, Divider } from "@mui/material";
import { CommonContext } from "../../../context/CommonContext";
import { AuthContext } from "../../../context/AuthContext";
import { apisPath } from "../../../utils/path";
import { authHttp } from "../../../services/commonHttpService";
import { Formik } from "formik";
import { treatmentHistorySchema } from "../../../schema/treatmentHistorySchema";
import { mapTreatmentHistoryResponse, treatmentHistoryInitialValues } from "../../../schema/treatmentHistoryInitialValues";

const TreatmentHistory = ({ submitForm, formResult, patientId, currentStep }) => {
  const { setIsLoading, setAlertState } = useContext(CommonContext);
  const { userId } = useContext(AuthContext);

  const formRef = useRef(null);
  const [initialValues, setInitialValues] = useState(treatmentHistoryInitialValues);
  const [isFocus, setIsFocus] = useState(false);

  // prepare payload for POST (kept inside the component)
  const refactorDataBeforeSubmit = (values) => {
    const payload = {
      patient_id: Number(patientId),
      user_id: Number(userId),
      ecog_status: values?.ecog_status,
      date_of_start: values?.date_of_start || null,
      date_of_end: values?.date_of_end || null,
      intention_of_treatment: values?.intention_of_treatment,
      role_of_treatment: values?.role_of_treatment,
      details: values?.details,
      status_of_treatment: values?.status_of_treatment,

      date_of_surgery: values?.date_of_surgery || null,
      primary_site: values?.primary_site,
      surgical_procedure_performed: values?.surgical_procedure_performed,
      surgeon_name: values?.surgeon_name,
      extent_of_resection: values?.extent_of_resection,
      tumor_margins: values?.tumor_margins,
      lymph_node_dissection: values?.lymph_node_dissection,

      chemo_regimen: values?.chemo_regimen,
      chemo_protocol_with_duration: values?.chemo_protocol_with_duration,
      chemo_planned_cycles: values?.chemo_planned_cycles,
      chemo_cycles_administered: values?.chemo_cycles_administered,

      radiotherapy_regimen: values?.radiotherapy_regimen,
      radiotherapy_protocol_name: values?.radiotherapy_protocol_name,
      radiotherapy_treatment_intent: values?.radiotherapy_treatment_intent,
      radiotherapy_total_dose: values?.radiotherapy_total_dose,
      radiotherapy_dose_per_fraction: values?.radiotherapy_dose_per_fraction,
      radiotherapy_no_of_planned_fractions: values?.radiotherapy_no_of_planned_fractions,
      radiotherapy_no_of_fractions_administered: values?.radiotherapy_no_of_fractions_administered,
      radiotherapy_treatment_site: values?.radiotherapy_treatment_site,
      radiotherapy_radiation_modality: values?.radiotherapy_radiation_modality,

      targeted_therapy_regimen_protocol: values?.targeted_therapy_regimen_protocol,
      targeted_therapy_treatment_intent: values?.targeted_therapy_treatment_intent,
      targeted_therapy_targeted_drug: values?.targeted_therapy_targeted_drug,
      targeted_therapy_molecular_target: values?.targeted_therapy_molecular_target,
      targeted_therapy_specific_dose: values?.targeted_therapy_specific_dose,
      targeted_therapy_route_of_administration: values?.targeted_therapy_route_of_administration,
      targeted_therapy_planned_cycles: values?.targeted_therapy_planned_cycles,
      targeted_therapy_administered_cycles: values?.targeted_therapy_administered_cycles,

      immunotherapy_regimen: values?.immunotherapy_regimen,
      immunotherapy_protocol_name: values?.immunotherapy_protocol_name,
      immunotherapy_treatment_intent: values?.immunotherapy_treatment_intent,
      immunotherapy_targeted_drug: values?.immunotherapy_targeted_drug,
      immunotherapy_molecular_target: values?.immunotherapy_molecular_target,
      immunotherapy_specific_dose: values?.immunotherapy_specific_dose,
      immunotherapy_route_of_administration: values?.immunotherapy_route_of_administration,
      immunotherapy_planned_cycles: values?.immunotherapy_planned_cycles,
      immunotherapy_administered_cycles: values?.immunotherapy_administered_cycles,

      hormone_therapy_regimen_protocol: values?.hormone_therapy_regimen_protocol,
      hormone_therapy_treatment_intent: values?.hormone_therapy_treatment_intent,
      hormone_therapy_immunotherapy: values?.hormone_therapy_immunotherapy,
      hormone_therapy_mechanism_of_action: values?.hormone_therapy_mechanism_of_action,
      hormone_therapy_specific_dose: values?.hormone_therapy_specific_dose,
      hormone_therapy_route_of_administration: values?.hormone_therapy_route_of_administration,
      hormone_therapy_planned_cycles: values?.hormone_therapy_planned_cycles,
      hormone_therapy_cycles_administered: values?.hormone_therapy_cycles_administered,

      version: 1,
      completed_step: 3,
    };

    return payload;
  };

  // GET existing data when this step becomes active (step index 2)
  useEffect(() => {
    if (currentStep === 2 && patientId) {
      fetchStepData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, patientId]);

  const fetchStepData = async () => {
    if (!patientId) return;
    setIsLoading(true);
    try {
      const res = await authHttp.get(`${apisPath.getPatientTreatmentStep}/${patientId}/3`);
      if (res?.data?.data) {
        setInitialValues(mapTreatmentHistoryResponse(res?.data?.data));
      }
    } catch (err) {
      if (err?.response?.status !== 404) {
        setAlertState({
          open: true,
          title: err?.response?.data?.message || "Something went wrong",
          btnTxt: "CLOSE",
          status: "fail",
          isSessionExpire: err?.response?.status === 401,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // parent trigger submit
  useEffect(() => {
    if (submitForm) {
      formRef?.current?.handleSubmit();

      setTimeout(() => {
        if (Object.keys(formRef?.current?.errors || {}).length !== 0) {
          setIsFocus(true);
        }
      }, 100);

      formResult(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitForm]);

  // focus & alert first error
  useEffect(() => {
    const errObj = formRef?.current?.errors || {};
    if (isFocus && Object.keys(errObj).length) {
      const keys = Object.keys(errObj);
      const firstField = window.document.getElementsByName(keys[0])[0];
      if (firstField) firstField.focus();

      setAlertState({
        open: true,
        title: Object.values(errObj)[0],
        btnTxt: "OK",
        status: "fail",
      });

      setIsFocus(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocus]);

  // submit handler
  const submitHandler = async (values) => {
    setIsLoading(true);
    try {
      const payload = refactorDataBeforeSubmit(values);
      const res = await authHttp.post(`${apisPath.patientTreatmentStep}/3`, payload);

      if (res?.data?.success || res?.status === 200) {
        setAlertState({
          open: true,
          title: "Treatment history saved successfully",
          btnTxt: "CLOSE",
          status: "success",
        });
        formResult(true);
      }
    } catch (err) {
      setAlertState({
        open: true,
        title: err?.response?.data?.message || "Something went wrong",
        btnTxt: "CLOSE",
        status: "fail",
        isSessionExpire: err?.response?.status === 401,
      });
      formResult(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="upload-clinical">
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={treatmentHistorySchema}
        onSubmit={submitHandler}
        innerRef={formRef}
      >
        {({ values, errors, touched, handleChange, setFieldValue }) => (
          <>
            <h6 className="header-sub mb-3">Treatment History</h6>

            {/* ECOG */}
            <div className="row mb-4 align-items-center">
              <div className="col-md-4 col-12 mb-3">
                <label className="form-label fw-semibold mb-2">ECOG Status</label>
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <label className="d-flex align-items-center gap-1">
                    <input
                      type="radio"
                      name="ecog_known"
                      value="known"
                      checked={values?.ecog_status && values?.ecog_status !== "Unknown"}
                      onChange={() => {
                        // Only update if currently Unknown or empty
                        if (!values?.ecog_status || values?.ecog_status === "Unknown") {
                          setFieldValue("ecog_status", "ECOG 1");
                        }
                      }}
                    />
                    Known
                  </label>

                  <label className="d-flex align-items-center gap-1">
                    <input
                      type="radio"
                      name="ecog_known"
                      value="unknown"
                      checked={values?.ecog_status === "Unknown"}
                      onChange={() => {
                        // Only update if not already Unknown
                        if (values?.ecog_status !== "Unknown") {
                          setFieldValue("ecog_status", "Unknown");
                        }
                      }}
                    />
                    Unknown
                  </label>
                </div>
              </div>

              {/* ECOG value dropdown */}
              {values?.ecog_status !== "Unknown" && (
                <div className="col-md-3 col-12 mb-3">
                  <TextField
                    fullWidth
                    select
                    label="Select ECOG Value"
                    size="small"
                    variant="standard"
                    name="ecog_status"
                    value={values?.ecog_status}
                    onChange={handleChange}  // Use handleChange instead of setFieldValue
                    // error={touched?.ecog_status && Boolean(errors?.ecog_status)}
                    // helperText={touched?.ecog_status && errors?.ecog_status}
                    className="ecog-dropdown"
                  >
                    <MenuItem value="ECOG 0">ECOG 0</MenuItem>
                    <MenuItem value="ECOG 1">ECOG 1</MenuItem>
                    <MenuItem value="ECOG 2">ECOG 2</MenuItem>
                    <MenuItem value="ECOG 3">ECOG 3</MenuItem>
                    <MenuItem value="ECOG 4">ECOG 4</MenuItem>
                    <MenuItem value="ECOG 5">ECOG 5</MenuItem>
                  </TextField>
                </div>
              )}
            </div>

            {/* Dates */}
            <div className="row mb-4">
              <div className="col-md-4 col-12 mb-3">
                <label htmlFor="date_of_start" className="form-label">Start Date</label>
                <input
                  type="date"
                  className={`form-control custom-date ${touched?.date_of_start && errors?.date_of_start ? "is-invalid" : ""}`}
                  id="date_of_start"
                  name="date_of_start"
                  value={values?.date_of_start}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                />
                {/* {touched?.date_of_start && errors?.date_of_start && (
                  <div className="invalid-feedback d-block">{errors?.date_of_start}</div>
                )} */}
              </div>

              <div className="col-md-4 col-12 mb-3">
                <label htmlFor="date_of_end" className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control custom-date"
                  id="date_of_end"
                  name="date_of_end"
                  value={values?.date_of_end}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>

            {/* Intention / Role / Details / Status */}
            <div className="row mb-4">
              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  select
                  label="Intention of Treatment"
                  size="small"
                  variant="standard"
                  name="intention_of_treatment"
                  value={values?.intention_of_treatment}
                  onChange={handleChange}
                >
                  <MenuItem value="Curative">Curative</MenuItem>
                  <MenuItem value="Palliative">Palliative</MenuItem>
                </TextField>
              </div>

              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  select
                  label="Role of Treatment"
                  size="small"
                  variant="standard"
                  name="role_of_treatment"
                  value={values?.role_of_treatment}
                  onChange={handleChange}
                >
                  <MenuItem value="Primary Chemotherapy">Primary Chemotherapy</MenuItem>
                  <MenuItem value="Primary Surgery">Primary Surgery</MenuItem>
                  <MenuItem value="Adjuvant">Adjuvant</MenuItem>
                </TextField>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-8 col-12">
                <TextField
                  fullWidth
                  label="Details"
                  placeholder="Enter treatment details"
                  size="small"
                  variant="standard"
                  name="details"
                  value={values?.details}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  select
                  label="Status of Treatment"
                  size="small"
                  variant="standard"
                  name="status_of_treatment"
                  value={values?.status_of_treatment}
                  onChange={handleChange}
                >
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="No treatment">No treatment</MenuItem>
                  <MenuItem value="Incomplete">Incomplete</MenuItem>
                  <MenuItem value="Treatment advised but not accepted">Treatment advised but not accepted</MenuItem>
                  <MenuItem value="Others">Others</MenuItem>
                </TextField>
              </div>
            </div>

            <Divider className="my-4" />
            <h6 className="header-sub mb-3">Surgery</h6>

            <div className="row mb-4">
              <div className="col-md-4 col-12">
                <label htmlFor="date_of_surgery" className="form-label">Date of Surgery</label>
                <input
                  type="date"
                  className="form-control custom-date"
                  id="date_of_surgery"
                  name="date_of_surgery"
                  value={values?.date_of_surgery}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>

              <div className="col-md-4 col-12 mt-4">
                <TextField
                  fullWidth
                  label="Primary Site"
                  placeholder="Enter primary site"
                  size="small"
                  variant="standard"
                  name="primary_site"
                  value={values?.primary_site}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12 mt-4">
                <TextField
                  fullWidth
                  label="Surgeon Name"
                  placeholder="Enter surgeon name"
                  size="small"
                  variant="standard"
                  name="surgeon_name"
                  value={values?.surgeon_name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Surgical procedure Performed"
                  placeholder="Enter procedure"
                  size="small"
                  variant="standard"
                  name="surgical_procedure_performed"
                  value={values?.surgical_procedure_performed}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  select
                  label="Extent Resection"
                  size="small"
                  variant="standard"
                  name="extent_of_resection"
                  value={values?.extent_of_resection}
                  onChange={handleChange}
                >
                  <MenuItem value="Complete">Complete</MenuItem>
                  <MenuItem value="Partial">Partial</MenuItem>
                </TextField>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  select
                  label="Tumour Margins"
                  size="small"
                  variant="standard"
                  name="tumor_margins"
                  value={values?.tumor_margins}
                  onChange={handleChange}
                >
                  <MenuItem value="Negative">Negative</MenuItem>
                  <MenuItem value="Positive">Positive</MenuItem>
                </TextField>
              </div>

              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  select
                  label="Lymph Node Dissection"
                  size="small"
                  variant="standard"
                  name="lymph_node_dissection"
                  value={values?.lymph_node_dissection}
                  onChange={handleChange}
                >
                  <MenuItem value="Performed">Performed</MenuItem>
                  <MenuItem value="Not Performed">Not Performed</MenuItem>
                </TextField>
              </div>
            </div>

            <Divider className="my-4" />
            <h6 className="header-sub mb-3">Chemotherapy</h6>

            <div className="row mb-4">
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Regimen"
                  size="small"
                  variant="standard"
                  name="chemo_regimen"
                  value={values?.chemo_regimen}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Protocol with Duration"
                  size="small"
                  variant="standard"
                  name="chemo_protocol_with_duration"
                  value={values?.chemo_protocol_with_duration}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Number of Cycles planned"
                  size="small"
                  variant="standard"
                  name="chemo_planned_cycles"
                  value={values?.chemo_planned_cycles}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Number of Cycles Administered"
                  size="small"
                  variant="standard"
                  name="chemo_cycles_administered"
                  value={values?.chemo_cycles_administered}
                  onChange={handleChange}
                />
              </div>
            </div>

            <Divider className="my-4" />
            <h6 className="header-sub mb-3">Radiotherapy</h6>

            <div className="row mb-4">
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Regimen"
                  size="small"
                  placeholder="Enter regimen"
                  variant="standard"
                  name="radiotherapy_regimen"
                  value={values?.radiotherapy_regimen}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Protocol Name"
                  size="small"
                  placeholder="Enter protocol"
                  variant="standard"
                  name="radiotherapy_protocol_name"
                  value={values?.radiotherapy_protocol_name}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  select
                  label="Treatment Intent"
                  size="small"
                  variant="standard"
                  name="radiotherapy_treatment_intent"
                  value={values?.radiotherapy_treatment_intent}
                  onChange={handleChange}
                >
                  <MenuItem value="Adjuvant">Adjuvant</MenuItem>
                  <MenuItem value="Neoadjuvant">Neoadjuvant</MenuItem>
                  <MenuItem value="Palliative">Palliative</MenuItem>
                </TextField>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Total Dose"
                  size="small"
                  placeholder="e.g.. 50 Gy"
                  variant="standard"
                  name="radiotherapy_total_dose"
                  value={values?.radiotherapy_total_dose}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Dose per Fraction"
                  size="small"
                  placeholder="e.g.. 2 Gy"
                  variant="standard"
                  name="radiotherapy_dose_per_fraction"
                  value={values?.radiotherapy_dose_per_fraction}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  label="Treatment Site"
                  size="small"
                  placeholder="Enter site"
                  variant="standard"
                  name="radiotherapy_treatment_site"
                  value={values?.radiotherapy_treatment_site}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Number of Fractions Planned"
                  size="small"
                  placeholder="0"
                  variant="standard"
                  name="radiotherapy_no_of_planned_fractions"
                  value={values?.radiotherapy_no_of_planned_fractions}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Number of Fractions Administered"
                  size="small"
                  placeholder="0"
                  variant="standard"
                  name="radiotherapy_no_of_fractions_administered"
                  value={values?.radiotherapy_no_of_fractions_administered}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  label="Radiation Modality"
                  size="small"
                  placeholder="0"
                  variant="standard"
                  name="radiotherapy_radiation_modality"
                  value={values?.radiotherapy_radiation_modality}
                  onChange={handleChange}
                />
              </div>
            </div>

            <Divider className="my-4" />
            <h6 className="header-sub mb-3">Targeted Therapy</h6>

            <div className="row mb-4">
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Regimen/Protocol Name"
                  size="small"
                  placeholder="Enter regimen"
                  variant="standard"
                  name="targeted_therapy_regimen_protocol"
                  value={values?.targeted_therapy_regimen_protocol}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Treatment Intent"
                  size="small"
                  placeholder="Enter intent"
                  variant="standard"
                  name="targeted_therapy_treatment_intent"
                  value={values?.targeted_therapy_treatment_intent}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  select
                  label="Treatment Drug"
                  size="small"
                  variant="standard"
                  name="targeted_therapy_targeted_drug"
                  value={values?.targeted_therapy_targeted_drug}
                  onChange={handleChange}
                >
                  <MenuItem value="Trastuzumab">Trastuzumab</MenuItem>
                  <MenuItem value="Drug 2">Drug 2</MenuItem>
                </TextField>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-3 col-12">
                <TextField
                  fullWidth
                  label="Molecular Target"
                  size="small"
                  placeholder="e.g.. HER2, EGFR"
                  variant="standard"
                  name="targeted_therapy_molecular_target"
                  value={values?.targeted_therapy_molecular_target}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 col-12">
                <TextField
                  fullWidth
                  label="Specific Dose"
                  size="small"
                  placeholder="Enter dose"
                  variant="standard"
                  name="targeted_therapy_specific_dose"
                  value={values?.targeted_therapy_specific_dose}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 col-12 mb-3">
                <TextField
                  fullWidth
                  select
                  label="Route of Administration"
                  size="small"
                  variant="standard"
                  name="targeted_therapy_route_of_administration"
                  value={values?.targeted_therapy_route_of_administration}
                  onChange={handleChange}
                >
                  <MenuItem value="IV">IV</MenuItem>
                  <MenuItem value="Oral">Oral</MenuItem>
                </TextField>
              </div>

              <div className="col-md-3 col-12 mb-3">
                <TextField
                  fullWidth
                  label="Cycles (Planned)"
                  size="small"
                  placeholder="0"
                  variant="standard"
                  name="targeted_therapy_planned_cycles"
                  value={values?.targeted_therapy_planned_cycles}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  select
                  label="Cycles Administered"
                  size="small"
                  variant="standard"
                  name="targeted_therapy_administered_cycles"
                  value={values?.targeted_therapy_administered_cycles}
                  onChange={handleChange}
                >
                  <MenuItem value="0">0</MenuItem>
                  <MenuItem value="1">1</MenuItem>
                </TextField>
              </div>
            </div>

            <Divider className="my-4" />
            <h6 className="header-sub mb-3">Immunotherapy</h6>

            <div className="row mb-4">
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Regimen"
                  size="small"
                  placeholder="Enter regimen"
                  variant="standard"
                  name="immunotherapy_regimen"
                  value={values?.immunotherapy_regimen}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Protocol Name"
                  size="small"
                  placeholder="Enter protocol"
                  variant="standard"
                  name="immunotherapy_protocol_name"
                  value={values?.immunotherapy_protocol_name}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Treatment Intent"
                  size="small"
                  placeholder="Enter Intent"
                  variant="standard"
                  name="immunotherapy_treatment_intent"
                  value={values?.immunotherapy_treatment_intent}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-3 col-12">
                <TextField
                  fullWidth
                  label="Immunotherapy Drug"
                  size="small"
                  placeholder="Enter drug name"
                  variant="standard"
                  name="immunotherapy_targeted_drug"
                  value={values?.immunotherapy_targeted_drug}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 col-12">
                <TextField
                  fullWidth
                  label="Mechanism of Action"
                  size="small"
                  placeholder="e.g,, PD-1 inhibitor"
                  variant="standard"
                  name="immunotherapy_molecular_target"
                  value={values?.immunotherapy_molecular_target}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 col-12 mb-3">
                <TextField
                  fullWidth
                  label="Specific Dose"
                  size="small"
                  placeholder="Enter dose"
                  variant="standard"
                  name="immunotherapy_specific_dose"
                  value={values?.immunotherapy_specific_dose}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 col-12 mb-3">
                <TextField
                  fullWidth
                  select
                  label="Route of Administration"
                  size="small"
                  variant="standard"
                  name="immunotherapy_route_of_administration"
                  value={values?.immunotherapy_route_of_administration}
                  onChange={handleChange}
                >
                  <MenuItem value="IV">IV</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  label="Number of Cycles Planned"
                  size="small"
                  placeholder="0"
                  variant="standard"
                  name="immunotherapy_planned_cycles"
                  value={values?.immunotherapy_planned_cycles}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  label="Number of Cycles Administered"
                  size="small"
                  placeholder="0"
                  variant="standard"
                  name="immunotherapy_administered_cycles"
                  value={values?.immunotherapy_administered_cycles}
                  onChange={handleChange}
                />
              </div>
            </div>

            <Divider className="my-4" />
            <h6 className="header-sub mb-3">Hormone Therapy</h6>

            <div className="row mb-4">
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Regimen/Protocol Name"
                  size="small"
                  placeholder="Enter regimen"
                  variant="standard"
                  name="hormone_therapy_regimen_protocol"
                  value={values?.hormone_therapy_regimen_protocol}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Treatment Intent"
                  size="small"
                  placeholder="Enter intent"
                  variant="standard"
                  name="hormone_therapy_treatment_intent"
                  value={values?.hormone_therapy_treatment_intent}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Hormone Therapy Drug"
                  size="small"
                  placeholder="Enter drug name"
                  variant="standard"
                  name="hormone_therapy_immunotherapy"
                  value={values?.hormone_therapy_immunotherapy}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-3 col-12">
                <TextField
                  fullWidth
                  label="Mechanism of Action"
                  size="small"
                  placeholder="Enter mechanism"
                  variant="standard"
                  name="hormone_therapy_mechanism_of_action"
                  value={values?.hormone_therapy_mechanism_of_action}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 col-12">
                <TextField
                  fullWidth
                  label="Specific Dose"
                  size="small"
                  placeholder="Enter dose"
                  variant="standard"
                  name="hormone_therapy_specific_dose"
                  value={values?.hormone_therapy_specific_dose}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 col-12 mb-3">
                <TextField
                  fullWidth
                  select
                  label="Route of Administration"
                  size="small"
                  variant="standard"
                  name="hormone_therapy_route_of_administration"
                  value={values?.hormone_therapy_route_of_administration}
                  onChange={handleChange}
                >
                  <MenuItem value="Oral">Oral</MenuItem>
                  <MenuItem value="IV">IV</MenuItem>
                </TextField>
              </div>

              <div className="col-md-3 col-12 mb-3">
                <TextField
                  fullWidth
                  label="Cycles Planned"
                  size="small"
                  placeholder="0"
                  variant="standard"
                  name="hormone_therapy_planned_cycles"
                  value={values?.hormone_therapy_planned_cycles}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  label="Cycles Administered"
                  size="small"
                  placeholder="0"
                  variant="standard"
                  name="hormone_therapy_cycles_administered"
                  value={values?.hormone_therapy_cycles_administered}
                  onChange={handleChange}
                />
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  );
};

export default TreatmentHistory;
